import React, { useState, useEffect } from "react";
import { Table, Button, Form, Row, Col, Spinner, OverlayTrigger, Tooltip, Modal } from "react-bootstrap";
import FilterBlock from "./FilterBlock";
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { RxCaretSort } from "react-icons/rx";
import ImageNoDataFound from "../../images/nodata_found.gif";
import ReactPaginate from "react-paginate";
import IconEdit from "../../images/Icons-edit.png";
import axios from "axios";
import apiConfig from "../credentials/apiConfig";
import { toast } from "react-toastify";
import PDFViewer from "./EngagementSetup/PdfViewer";

const itemsPerPage = 10;

const TableComponent = ({
  isClientModalOpen,
  isEngagementModalOpen,
  setCounts,
  onEditClick,
  isChecked,
}) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    industry: "All Industries",
    yearEnd: [],
    status: "Status",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quickLinkLoading, setQuickLinkLoading] = useState(null);

  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfTitle, setPdfTitle] = useState("");
  
  // Modal state for PDF ready
  const [showPdfReadyModal, setShowPdfReadyModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfType, setPdfType] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {

      const safeSorting =
        sortConfig?.key && (sortConfig.direction === "asc" || sortConfig.direction === "desc")
          ? { key: sortConfig.key, order: sortConfig.direction === "asc" ? 1 : -1 }
          : { key: "createdAt", order: -1 };

      const authToken = localStorage.getItem("authToken");
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/allEngagementsClientsList`,
        {
          page: currentPage + 1,
          perPage: itemsPerPage,
          searchString: filters.search,
          sorting: safeSorting,
          filters: {
            industry:
              filters.industry !== "All Industries" ? [filters.industry] : [],
            status:
              filters.status !== "Status"
                ? [filters.status.toLowerCase()]
                : [],
            engagementYear:
              filters.yearEnd.length > 0 ? filters.yearEnd.map(Number) : [],
          },
        },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      const engagements = response.data?.clients?.[0]?.engagements || [];
      const counts = response.data?.clients?.[0]?.counts?.[0] || {};
      const formattedEngagements = engagements.map((engagement) => {
        const quickLinkStatus = engagement.quickLinkStatus || {};
        const quickLinks = [];

        if (quickLinkStatus.par?.status === "completed") {
          quickLinks.push("PAR");
        }
        if (quickLinkStatus.far?.status === "completed") {
          quickLinks.push("FAR");
        }

        return {
          ...engagement,
          id: engagement._id,
          links: quickLinks,
        };
      });

      setData(formattedEngagements);
      setFilteredData(formattedEngagements);
      setTotalItems(counts.totalEngagements || engagements.length);
      setTotalPages(Math.ceil((counts.totalEngagements || engagements.length) / itemsPerPage));

      if (counts) {
        setCounts({
          totalEngagements: counts.totalEngagements || 0,
          auditEngagements: counts.auditEngagements || 0,
          reviewEngagements: counts.reviewEngagements || 0,
          closedEngagements: counts.closedEngagements || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching engagements:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    currentPage,
    filters,
    sortConfig,
    isClientModalOpen,
    isEngagementModalOpen,
    isChecked,
  ]);

  const handleStatusChange = async (id, newStatus) => {
    const authToken = localStorage.getItem("authToken");
    try {
      const response = await axios.put(
        `${apiConfig.apiBaseURL}/updateEngagementStatus/${id}`,
        { status: newStatus },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.success) {
        toast.success("Status updated successfully!", { autoClose: 3000 });
        fetchData();
      } else {
        toast.error(`Failed to update status: ${response.data.message}`, {
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(`Error updating status: ${error.message}`, {
        autoClose: 3000,
      });
    }
  };

  // Handle Quick Link Click
// Handle Quick Link Click
const handleQuickLinkClick = async (linkType, engagementId) => {
  setQuickLinkLoading(`${linkType}-${engagementId}`);
  setShowPdfReadyModal(false);
  
  try {
    const authToken = localStorage.getItem("authToken");

    const response = await axios.post(
      `${apiConfig.apiBaseURL}/exportPARFAR/${engagementId}`,
      {
        type: linkType.toLowerCase(),
        format: 'pdf'
      },
      {
        headers: {
          "x-api-key": apiConfig["x-api-key"],
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        timeout: 180000
      }
    );

    if (response.data && response.data.downloadUrl) {
      // ✅ Always show modal - don't auto-open
      setPdfUrl(response.data.downloadUrl);
      setPdfType(linkType);
      setShowPdfReadyModal(true);
    } else {
      throw new Error(response.data?.message || `Failed to load ${linkType} report`);
    }
  } catch (error) {
    console.error(`Error fetching ${linkType} report:`, error);
    
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      toast.error(`Request timed out while loading ${linkType} report. Please try again.`, { autoClose: 6000 });
    } else if (error.response) {
      toast.error(error.response.data?.message || `Failed to load ${linkType} report`, { autoClose: 5000 });
    } else if (error.request) {
      toast.error("No response from server. Please check your connection.", { autoClose: 5000 });
    } else {
      toast.error(`Error loading ${linkType} report: ${error.message}`);
    }
  } finally {
    setQuickLinkLoading(null);
  }
};

  const sortTable = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? (
        <MdKeyboardArrowUp size={20} />
      ) : (
        <MdKeyboardArrowDown size={20} />
      );
    }
    return <RxCaretSort size={20} />;
  };

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
    setCurrentPage(0);
  };

  const displayedData = filteredData;


  return (
    <>
      <FilterBlock onApply={handleApplyFilters} searchValue={filters.search} />

      <Table hover responsive style={{ border: "2px solid #E7ECF1" }} className="engagements-table">
        <thead>
          <tr style={{ whiteSpace: "nowrap" }}>
            <th
              onClick={() => sortTable("clientName")}
              className="text-light py-3"
            >
              Client Name {getSortIcon("clientName")}
            </th>
            <th
              onClick={() => sortTable("engagementYear")}
              className="text-light py-3"
            >
              Engagement Year {getSortIcon("engagementYear")}
            </th>
            <th
              onClick={() => sortTable("engagementType")}
              className="text-light py-3"
            >
              Engagement Type {getSortIcon("engagementType")}
            </th>
            <th
              onClick={() => sortTable("industry")}
              className="text-light py-3"
            >
              Industry {getSortIcon("industry")}
            </th>
            <th onClick={() => sortTable("status")} className="text-light py-3">
              Status {getSortIcon("status")}
            </th>
            <th className="text-light py-3 text-center">Quick Links</th>
          </tr>
        </thead>

        <tbody className="credential-page">
          {loading ? (
            <tr>
              <td colSpan="6" className="text-center p-5">
                <Spinner animation="border" variant="primary" />
              </td>
            </tr>
          ) : displayedData.length > 0 ? (
            displayedData.map((row) => (
              <tr key={row.id} style={{ whiteSpace: "nowrap" }}>
                <td className="text-primary align-middle" onClick={() => onEditClick(row._id)}
                  style={{ cursor: "pointer" }}>
                  <strong>{row.clientName}</strong>
                </td>
                <td className="text-light align-middle">{row.engagementYear}</td>
                <td className="text-light align-middle">{row.engagementType}</td>
                <td className="text-light align-middle">{row.industry}</td>
                <td className="text-light" style={{ width: '120px' }}>
                  <Form.Select
                    value={row.status}
                    onChange={(e) => handleStatusChange(row.id, e.target.value)}
                    className={row.status ? `status-${row.status.toLowerCase()}` : ""}

                  >
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </Form.Select>
                </td>
                <td
                  className="text-light d-flex justify-content-center"
                  style={{ paddingTop: "17px", paddingBottom: "19px" }}
                >
                  <div>
                    {row.links?.length > 0 ? (
                      row.links.map((link, index) => (
                        <Button
                          key={index}
                          variant="outline-primary"
                          size="sm"
                          className="me-2 d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleQuickLinkClick(link, row._id)}
                          disabled={quickLinkLoading === `${link}-${row._id}`}
                        >
                          {link}
                          {quickLinkLoading === `${link}-${row._id}` && (
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="ms-1"
                            />
                          )}
                        </Button>

                      ))
                    ) : (
                      <span className="text-muted">No Links</span>
                    )}
                  </div>
                  <div className="me-3">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`tooltip-edit-${row._id}`}>Edit Engagement</Tooltip>}
                    >
                      <img
                        src={IconEdit}
                        className="ms-3"
                        alt="edit"
                        onClick={() => onEditClick(row._id)}
                        style={{ cursor: "pointer" }}
                      />
                    </OverlayTrigger>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="text-center text-muted p-3">
                <div>
                  <img src={ImageNoDataFound} alt="No data found" />
                  <p className="fw-bold text-dark mb-0">No data found</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {!loading && filteredData.length > 0 && (
        <Row className="d-flex justify-content-between align-items-center mt-3">
          <Col md={6}>
            <p>
              Showing {totalItems === 0 ? 0 : currentPage * itemsPerPage + 1}
              –
              {Math.min((currentPage + 1) * itemsPerPage, totalItems)} of {totalItems}
            </p>

          </Col>
          <Col md={6} className="d-flex justify-content-end">
            <ReactPaginate
              previousLabel={"<"}
              nextLabel={">"}
              breakLabel="..."
              pageCount={totalPages}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"pagination"}
              activeClassName={"active"}
              forcePage={currentPage}
            />
          </Col>
        </Row>
      )}

      {/* PDF Ready Modal */}
      <Modal show={showPdfReadyModal} onHide={() => setShowPdfReadyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>PDF Ready!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            Your {pdfType} report has been generated successfully.
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              window.open(pdfUrl, '_blank', 'noopener,noreferrer');
              setShowPdfReadyModal(false);
            }}
            style={{ padding: '12px 40px', fontSize: '16px' }}
          >
            Open PDF in New Tab
          </Button>
        </Modal.Body>
      </Modal>

      <PDFViewer
        show={showPdfViewer}
        onHide={() => setShowPdfViewer(false)}
        pdfBlob={pdfBlob}
        title={pdfTitle}
      />
    </>
  );
};

export default TableComponent;